/**
 * The local console.
 *
 * ⛔ BINDS LOOPBACK ONLY, AND THE PAGE SAYS SO. There is no authentication because this must never be
 * reachable from anywhere else. The tool it is modelled on calls `app.listen(port)` with no host,
 * which binds every interface; that is the bug this file exists not to have.
 *
 * ⛔ NO ENDPOINT RETURNS A CREDENTIAL. `/api/state` reports presence and the last four characters,
 * and it gets both from `credentialHint` rather than deciding again here — a second implementation of
 * "how much of a secret is safe to show" is a second thing to get wrong. A leaked token is not
 * recoverable, so the page must not be able to display one.
 *
 * ⛔ THE QUEUE SERVED HERE IS THE REAL ONE ON DISK, AND THERE IS NO FIXTURE FALLBACK. This endpoint
 * used to return `SAMPLE_FINDINGS` with a `findingsAreFixtures: true` flag beside it. That flag was
 * honest only while the store was unbuilt; once a real queue existed at `triage.db` the console was
 * showing invented findings over a live installation, and a field that announces "these are
 * fixtures" is worse than useless the moment it is lying. An empty store is reported as an empty
 * queue with the reason it is empty. It is never filled in with something that reads like review
 * output.
 *
 * ⛔ THE TRIAGE VERBS ARE THE CLI'S VERBS. accept and dismiss, a dismissal reason from the fixed
 * vocabulary, and an ambiguous fingerprint prefix refused rather than resolved to the first match —
 * the same semantics as `sibyl triage`, because two surfaces onto one queue that disagree about
 * what a decision means is how an audit trail stops being one.
 */

import { createServer, type IncomingMessage, type ServerResponse } from "node:http"
import { readFileSync } from "node:fs"
import { loadConfig } from "../config/load.js"
import { isLoopback, type SibylConfig } from "../config/schema.js"
import { credentialHint } from "../config/secrets.js"
import {
  assertDismissReason,
  openTriageStore,
  type TriageRecord,
  type TriageStore,
} from "../triage/store.js"
import { DISMISS_REASONS, type Severity } from "../types/finding.js"
import { PAGE } from "./page.js"

/** Presence and a fingerprint. Never a value. */
function credentialState(
  names: readonly string[],
  env: NodeJS.ProcessEnv,
): Array<{ name: string; set: boolean; hint: string }> {
  return names.map((name) => {
    const v = env[name]
    return {
      name,
      set: typeof v === "string" && v.length > 0,
      // ⛔ The hint comes from src/config/secrets.ts, which is also what the CLI banner uses. Short
      // values report presence only; four characters of a short value is most of it.
      hint: credentialHint(v),
    }
  })
}

export interface ServeOptions {
  readonly port?: number
  readonly host?: string
  /**
   * The resolved configuration. Defaults to loading it, which is what `sibyl web` wants; a test
   * passes one whose `triage.db` points at a temp file, so the console is exercised against a real
   * store rather than a mock of one.
   */
  readonly config?: SibylConfig
  /**
   * Where credential presence is read from. Injected so a test can set a value and then assert that
   * no response carries it — an assertion that cannot be made against the ambient environment.
   */
  readonly env?: NodeJS.ProcessEnv
  /** Forwarded to `openTriageStore`. A test asks for `json` so the store it seeded is the one read. */
  readonly storeBackend?: "auto" | "sqlite" | "json"
}

// ── The queue, as the page needs it ────────────────────────────────────────────────────────────

/**
 * ⚠️ Ordering is decided here rather than left to the store's insertion order, because the page is
 * driven by keyboard position: a list that reorders itself between two refreshes moves the row under
 * `j`/`k` and a person accepts the finding below the one they were reading.
 */
const SEVERITY_RANK: Record<Severity, number> = { high: 0, medium: 1, low: 2 }
const STATE_RANK: Record<TriageRecord["state"]["kind"], number> = { todo: 0, accepted: 1, dismissed: 2 }

function ordered(records: readonly TriageRecord[]): TriageRecord[] {
  return [...records].sort(
    (a, b) =>
      STATE_RANK[a.state.kind] - STATE_RANK[b.state.kind] ||
      SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
      a.lastAnchor.path.localeCompare(b.lastAnchor.path) ||
      a.lastAnchor.line - b.lastAnchor.line ||
      a.fingerprint.localeCompare(b.fingerprint),
  )
}

/**
 * ⛔ COUNTS CARRY THEIR DENOMINATOR. "3 waiting" over a queue of 3 and over a queue of 40 are
 * different situations, and the second one is the one worth knowing about. `sibyl triage` states
 * what its filter hid for the same reason.
 */
interface QueueCounts {
  readonly todo: number
  readonly accepted: number
  readonly dismissed: number
  readonly total: number
}

interface QueueView {
  readonly path: string
  readonly backend: string
  readonly counts: QueueCounts
  readonly empty: boolean
  /** The sentence the page prints above the list. Always present, in both states. */
  readonly note: string
  readonly findings: readonly TriageRecord[]
}

function queueView(store: TriageStore): QueueView {
  const all = store.all()
  const counts: QueueCounts = {
    todo: all.filter((r) => r.state.kind === "todo").length,
    accepted: all.filter((r) => r.state.kind === "accepted").length,
    dismissed: all.filter((r) => r.state.kind === "dismissed").length,
    total: all.length,
  }
  return {
    path: store.backend.path,
    backend: store.backend.kind,
    counts,
    empty: all.length === 0,
    // ⛔ The empty case says WHY it is empty. "No findings" alone reads as a clean review, which is
    // the one thing an unrun review must never be mistaken for.
    note:
      all.length === 0
        ? "The triage queue is empty because a review has not been run yet. Run `sibyl review <pr>` and its findings appear here. Nothing is shown on this page that a review did not produce."
        : `${counts.todo} of ${counts.total} findings are waiting on a decision.`,
    findings: ordered(all),
  }
}

// ── Reading a request ──────────────────────────────────────────────────────────────────────────

/** Anything thrown with a status is answered with that status; anything else is a 500. */
class HttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly extra: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = "HttpError"
  }
}

/**
 * ⛔ Capped. The body is accumulated in memory, and an uncapped accumulator on a listening socket is
 * a way to exhaust this process from any program on the machine. 64 KiB is orders of magnitude above
 * the largest real request, which is a fingerprint and a reason.
 */
const MAX_BODY_BYTES = 64 * 1024

function readBody(req: IncomingMessage): Promise<string> {
  return new Promise((resolve, reject) => {
    const chunks: Buffer[] = []
    let bytes = 0
    req.on("data", (c: Buffer) => {
      bytes += c.length
      if (bytes > MAX_BODY_BYTES) {
        reject(new HttpError(413, `request body over ${MAX_BODY_BYTES} bytes`))
        req.destroy()
        return
      }
      chunks.push(c)
    })
    req.on("end", () => resolve(Buffer.concat(chunks).toString("utf8")))
    req.on("error", reject)
  })
}

function asObject(body: string): Record<string, unknown> {
  let parsed: unknown
  try {
    parsed = JSON.parse(body === "" ? "null" : body)
  } catch (e) {
    throw new HttpError(400, `the request body is not JSON (${(e as Error).message})`)
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new HttpError(400, "the request body must be a JSON object")
  }
  return parsed as Record<string, unknown>
}

/**
 * ⭐ A prefix is accepted because nobody retypes a full fingerprint, but an AMBIGUOUS prefix is
 * refused rather than resolved to the first match — lifted from `sibyl triage`, where silently
 * acting on the wrong finding is the one outcome the command must never produce. The matches are
 * listed in the refusal so the caller can lengthen the prefix without going to look them up.
 */
function resolveFingerprint(store: TriageStore, target: string): string {
  const matches = store.all().filter((r) => r.fingerprint.startsWith(target))
  if (matches.length === 0) {
    throw new HttpError(404, `no finding whose fingerprint starts with ${JSON.stringify(target)}`)
  }
  if (matches.length > 1) {
    throw new HttpError(400, `${matches.length} findings start with ${JSON.stringify(target)}; use more characters`, {
      matches: matches.map((m) => m.fingerprint),
    })
  }
  return (matches[0] as TriageRecord).fingerprint
}

function applyTriage(store: TriageStore, body: Record<string, unknown>, who: string): TriageRecord {
  const fingerprint = body["fingerprint"]
  if (typeof fingerprint !== "string" || fingerprint.trim() === "") {
    throw new HttpError(400, "a triage decision needs a fingerprint")
  }
  const action = body["action"]
  // ⛔ Two verbs, the same two the CLI has. A third one here would be a decision `sibyl triage`
  // cannot make and cannot show.
  if (action !== "accept" && action !== "dismiss") {
    throw new HttpError(400, `action must be accept or dismiss, got ${JSON.stringify(action ?? null)}`, {
      allowed: ["accept", "dismiss"],
    })
  }
  const fp = resolveFingerprint(store, fingerprint.trim())

  if (action === "accept") return store.accept(fp, who)

  const reason = body["reason"]
  // ⛔ A dismissal without a reason is refused, not defaulted. The dismissal record exists to tell a
  // false positive apart from a real finding somebody chose not to fix, and a defaulted reason
  // answers that question with a guess.
  if (typeof reason !== "string" || reason === "") {
    throw new HttpError(400, `a dismissal needs a reason, one of: ${DISMISS_REASONS.join(", ")}`, {
      allowed: DISMISS_REASONS,
    })
  }
  try {
    assertDismissReason(reason)
  } catch (e) {
    throw new HttpError(400, e instanceof Error ? e.message : String(e), { allowed: DISMISS_REASONS })
  }
  const note = body["note"]
  return store.dismiss(fp, {
    reason,
    by: who,
    ...(typeof note === "string" && note.trim() !== "" ? { note: note.trim() } : {}),
  })
}

// ── The server ─────────────────────────────────────────────────────────────────────────────────

export function startServer(opts: ServeOptions = {}): ReturnType<typeof createServer> {
  const config = opts.config ?? loadConfig({ noUserConfig: true }).config
  const env = opts.env ?? process.env
  const host = opts.host ?? config.server.bind
  const port = opts.port ?? config.server.port

  // ⛔ Defence in depth. Validation already refuses a non-loopback bind without the explicit
  // opt-in; this refuses to start even if that check is ever bypassed.
  if (!isLoopback(host) && !config.server.allowNonLoopback) {
    throw new Error(
      `refusing to bind ${host}: this surface has no authentication. Set server.allow_non_loopback only if you mean it.`,
    )
  }

  // ⚠️ `USER` can be unset in a container. Recorded as `unknown` rather than left blank, so the
  // record still says a person decided and does not read as a machine decision. Same fallback as
  // `sibyl triage`, so a decision made in the console and one made at the shell are attributed
  // identically.
  const who = env["USER"] ?? "unknown"

  /**
   * ⛔ OPENED ONCE, FOR THE LIFE OF THE SERVER. Opening per request would re-read the whole store on
   * every keystroke and — with the JSON backend, which rewrites the file whole — let two in-flight
   * requests each write back a set that never saw the other's decision. Every handler awaits this
   * one promise.
   */
  const storeOnce = openTriageStore({
    path: config.triage.db,
    ...(opts.storeBackend === undefined ? {} : { backend: opts.storeBackend }),
  })
  // The rejection is reported per request, by the handler that awaited it. This only marks the
  // promise handled so an unopenable store does not take the process down before anyone asks.
  storeOnce.catch(() => undefined)

  const locks = [config.post.enabled === false, config.post.dryRun === true, config.triage.requireHuman]
  const surface = {
    repo: config.repo.slug || "(auto-detect)",
    dimensions: config.dimensions.map((d) => ({ id: d.id, title: d.title, enabled: d.enabled })),
    credentials: credentialState(config.secrets.keys, env),
    locks: { engaged: locks.filter(Boolean).length, total: locks.length },
    /** The page needs these to build the dismissal picker; the vocabulary lives in one place. */
    dismissReasons: DISMISS_REASONS,
  }

  const send = (res: ServerResponse, code: number, type: string, body: string): void => {
    res.writeHead(code, {
      "content-type": type,
      // Nothing here should be cached by anything.
      "cache-control": "no-store",
      // The page is self-contained: no CDN, no remote font, no external asset.
      "content-security-policy": "default-src 'self'; img-src 'self'; style-src 'unsafe-inline'; script-src 'unsafe-inline'",
    })
    res.end(body)
  }

  const sendJson = (res: ServerResponse, code: number, value: unknown): void =>
    send(res, code, "application/json", JSON.stringify(value))

  const handle = async (req: IncomingMessage, res: ServerResponse): Promise<void> => {
    const url = req.url ?? "/"
    if (url === "/" || url.startsWith("/?")) return send(res, 200, "text/html; charset=utf-8", PAGE)
    if (url.startsWith("/assets/")) {
      // ⛔ Vendored, not fetched. The page must work with no network beyond the APIs it calls.
      const name = url.slice("/assets/".length)
      if (!/^[a-z0-9-]+\.jpg$/.test(name)) return send(res, 400, "text/plain", "bad asset\n")
      try {
        const buf = readFileSync(new URL(`../../assets/${name}`, import.meta.url))
        res.writeHead(200, { "content-type": "image/jpeg", "cache-control": "max-age=3600" })
        res.end(buf)
        return
      } catch {
        return send(res, 404, "text/plain", "no asset\n")
      }
    }

    if (url === "/api/state" || url.startsWith("/api/state?")) {
      const store = await storeOnce
      return sendJson(res, 200, { ...surface, queue: queueView(store) })
    }

    if (url === "/api/triage" || url.startsWith("/api/triage?")) {
      if (req.method !== "POST") {
        return send(res, 405, "text/plain", "use POST\n")
      }
      const body = asObject(await readBody(req))
      const store = await storeOnce
      const record = applyTriage(store, body, who)
      // ⭐ The updated record AND the recomputed queue. The page renders from what came back rather
      // than from what it assumed the click did, so a decision the store refused can never sit on
      // screen looking as though it was recorded.
      return sendJson(res, 200, { record, queue: queueView(store) })
    }

    send(res, 404, "text/plain", "not found\n")
  }

  const server = createServer((req: IncomingMessage, res: ServerResponse) => {
    void handle(req, res).catch((e: unknown) => {
      if (res.headersSent) {
        res.end()
        return
      }
      const status = e instanceof HttpError ? e.status : 500
      const message = e instanceof Error ? e.message : String(e)
      const extra = e instanceof HttpError ? e.extra : {}
      // ⛔ The message names what was wrong with the request, never what the server holds. A 500 here
      // is almost always an unopenable store, and its message names the FILE, never a record.
      sendJson(res, status, { error: message, ...extra })
    })
  })

  // ⛔ The store is released with the server. The JSON backend has nothing to release, but the
  // SQLite one holds a file handle, and a console restarted in a loop would leak one per start.
  server.on("close", () => {
    void storeOnce.then((s) => s.close()).catch(() => undefined)
  })

  server.listen(port, host)
  return server
}
